import * as THREE from 'three';

/**
 * RiggedManager - Mapping MediaPipe vers Mixamo
 * Basé sur: https://gist.github.com/mcwachanga/fed775e4068f66fcad92e15c90cae827
 */
export class RiggedManager {
    constructor(model, boneMapping, config = {}) {
        this.model = model;
        this.config = {
            enabled: config.enabled !== false,
            showDebugSpheres: config.showDebugSpheres !== false,
            scaleFactor: config.scaleFactor || 1.0,
            ...config
        };
        
        // Mapping exact depuis l'exemple GitHub
        // Index -> Nom du bone Mixamo (préfixe "mixamorig" ajouté)
        this.bodySkeleton = {};
        
        this.bones = {};
        this.debugSpheres = {};
        this.initialized = false;
        
        if (this.model && this.config.enabled) {
            this.initialize();
        }
    }

    initialize() {
        console.log('🦴 INIT RIGGED MANAGER (MediaPipe->Mixamo)');
        
        // DEBUG : Lister TOUS les bones du modèle
        console.log('🔍 DEBUG - Liste de TOUS les bones dans le modèle :');
        const allBones = [];
        this.model.traverse((child) => {
            if (child.isBone) {
                allBones.push(child.name);
                console.log(`   📍 Bone trouvé: "${child.name}"`);
            }
        });
        console.log(`   ✅ Total: ${allBones.length} bones trouvés`);
        console.log('');
        
        this.mapBodySkeleton();
        this.cacheBones();
        this.createDebugSpheres();
        this.initialized = Object.keys(this.bones).length > 0;
        console.log(`✅ ${Object.keys(this.bones).length} bones mappés avec succès`);
        if (this.config.showDebugSpheres) {
            console.log(`🔴 ${Object.keys(this.debugSpheres).length} sphères de debug créées`);
        }
    }

    mapBodySkeleton() {
        // Mapping depuis positionsPose3d (noms de propriétés) -> bones Mixamo
        // Utilise les noms directement depuis resultsManagerPose
        
        this.bodySkeleton = {
            // Visage
            "nose": "mixamorigNeck",
            "left_eye": "mixamorigHead",
            "right_eye": "mixamorigHead",
            "left_eye_outer": "mixamorigLeftEye",
            "right_eye_outer": "mixamorigRightEye",
            "left_ear": "mixamorigHeadTop_End",
            "right_ear": "mixamorigHeadTop_End",
            "left_mouth": "mixamorigNeck",
            "right_mouth": "mixamorigNeck",
            
            // Épaules et bras
            "left_shoulder": "mixamorigLeftShoulder",
            "right_shoulder": "mixamorigRightShoulder",
            "left_elbow": "mixamorigLeftArm",
            "right_elbow": "mixamorigRightArm",
            "left_wrist": "mixamorigLeftForeArm",
            "right_wrist": "mixamorigRightForeArm",
            "left_hand": "mixamorigLeftHand",
            "right_hand": "mixamorigRightHand",
            "left_pinky": "mixamorigLeftHand",
            "right_pinky": "mixamorigRightHand",
            "left_index": "mixamorigLeftHand",
            "right_index": "mixamorigRightHand",
            "left_thumb": "mixamorigLeftHand",
            "right_thumb": "mixamorigRightHand",
            
            // Hanches et jambes
            "left_hip": "mixamorigLeftUpLeg",
            "right_hip": "mixamorigRightUpLeg",
            "left_knee": "mixamorigLeftLeg",
            "right_knee": "mixamorigRightLeg",
            "left_ankle": "mixamorigLeftFoot",
            "right_ankle": "mixamorigRightFoot",
            "left_heel": "mixamorigLeftToeBase",
            "right_heel": "mixamorigRightToeBase",
            "left_foot_index": "mixamorigLeftToe_End",
            "right_foot_index": "mixamorigRightToe_End",
            
            // Positions calculées bonus
            "center_hips": "mixamorigHips",
            "center_shoulders": "mixamorigSpine2"
        };
        
        console.log('📋 Mapping MediaPipe->Mixamo configuré (positionsPose3d avec préfixe mixamorig)');
    }

    cacheBones() {
        // Cache les bones depuis le modèle en utilisant le bodySkeleton
        for (const [landmarkName, boneName] of Object.entries(this.bodySkeleton)) {
            const bone = this.getSkeletonFromMesh(boneName);
            if (bone) {
                this.bones[landmarkName] = bone;
                console.log(`  ✓ ${landmarkName} -> ${boneName} (${bone.name})`);
            } else {
                console.warn(`  ⚠️ Bone non trouvé: ${boneName} pour ${landmarkName}`);
            }
        }
    }

    getSkeletonFromMesh(name) {
        // Chercher un bone par son nom
        // Maintenant avec des noms complets (mixamorigXXX)
        let bone = null;
        
        this.model.traverse((child) => {
            if (child.isBone && child.name === name) {
                bone = child;
            }
        });
        
        // Debug : signaler si non trouvé
        if (!bone) {
            console.warn(`   ❌ Bone NON trouvé: "${name}"`);
        }
        
        return bone;
    }

    createDebugSpheres() {
        if (!this.config.showDebugSpheres) return;

        let scene = this.model;
        while (scene.parent) {
            scene = scene.parent;
        }

        for (const [index, bone] of Object.entries(this.bones)) {
            const geometry = new THREE.SphereGeometry(10, 16, 16); 
            const material = new THREE.MeshBasicMaterial({ 
                color: 0xff0000,
                transparent: true,
                opacity: 0.8,
                depthTest: false,
                depthWrite: false
            });
            
            const sphere = new THREE.Mesh(geometry, material);
            sphere.name = `DebugSphere_${index}`;
            sphere.renderOrder = 9999;
            
            scene.add(sphere);
            this.debugSpheres[index] = sphere;
            
            console.log(`  🔴 Sphère créée pour ${index}`);
        }
    }

    update(coordsResults) {
        if (!this.initialized || !this.config.enabled) return;
        
        const pose = coordsResults?.pose;
        if (!pose?.poseVisible) return;

        // Utiliser positionsPose3d qui contient déjà des Vector3 Three.js
        const positions = pose.positionsPose2d;
        
        if (!positions) {
            console.warn('⚠️ Pas de positionsPose3d disponibles');
            return;
        }

        // Appliquer les positions directement
        this.transformBodySkeleton(positions);
    }

    transformBodySkeleton(positions) {
        // Itérer sur toutes les propriétés de positionsPose3d
        for (const [landmarkName, position] of Object.entries(positions)) {
            const bone = this.bones[landmarkName];
            
            if (bone && position) {
                // Application directe des coordonnées avec le facteur d'échelle
                // bone.position = position LOCALE du bone (relative au parent)
                bone.position.x = position.x * this.config.scaleFactor;
                bone.position.y = position.y * this.config.scaleFactor;
                bone.position.z = position.z * this.config.scaleFactor;
                
                // Mettre à jour la sphère de debug avec position world pour visualisation
                const sphere = this.debugSpheres[landmarkName];
                if (sphere) {
                    // Pour le debug, on utilise la position world du bone
                    const worldPos = new THREE.Vector3();
                    bone.getWorldPosition(worldPos);
                    sphere.position.copy(worldPos);
                    sphere.visible = true;
                }
            }
        }
    }

    dispose() {
        console.log('🗑️ Dispose RiggedManager');
        
        for (const [index, sphere] of Object.entries(this.debugSpheres)) {
            if (sphere.parent) {
                sphere.parent.remove(sphere);
            }
            sphere.geometry.dispose();
            sphere.material.dispose();
        }
        this.debugSpheres = {};
        this.bones = {};
        this.bodySkeleton = {};
        this.initialized = false;
    }

    isInitialized() {
        return this.initialized;
    }

    getBoneCount() {
        return Object.keys(this.bones).length;
    }
}
